'use client';

import _ from 'lodash';
import Script from 'next/script';
import { FC, useCallback, useEffect, useState } from 'react';
import { ThemeProvider } from 'styled-components';

import { CalmTheme } from '@calm-web/design-system';

import InitialPageAnalytics from '@/components/analytics/InitialPageAnalytics';
import { EnvConfig } from '@/env_config';
import { useAnalytics } from '@/hooks/analytics/useAnalytics';
import useDiscountedPricing from '@/hooks/api/useDiscountedPricing';
import { TEAMS_USER_INFO_KEY, useStoreSsCheckoutProgress } from '@/hooks/useStoreSsCheckoutProgress';
import CalmCookie from '@/utils/CalmCookie';
import { calmLogger } from '@/utils/calmLogger';

import Company from './Company';
import CoveredLives from './CoveredLives';
import Info from './Info';
import PaymentWithPaymentElement from './PaymentWithPaymentElement';
import { Body, CalmLogo, Container, Content, FixedTopBar, Wrapper } from './styles';
import TeamsHero from './TeamsHero';
import {
	CompanyFormFields,
	CoveredLivesCount,
	InfoFormFields,
	NavigateTo,
	TeamsPageName,
	TeamsPageNameHash,
} from './types';
import { calculatingSelectedContractedLives } from './utils';

declare global {
	interface Window {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		fbq: any;
	}
}

function getActivePage(hash: string): TeamsPageNameHash {
	if (hash === '') {
		return '#coveredLives';
	}
	return hash as TeamsPageNameHash;
}

export const determineNavigateToPage = (fields: {
	firstName?: string;
	lastName?: string;
	workEmail?: string;
	company?: string;
	companySize?: string;
	jobTitle?: string;
	coveredLives?: number;
}): TeamsPageName => {
	const { firstName, lastName, workEmail, company, companySize, jobTitle, coveredLives } = fields;
	if (!coveredLives) return 'coveredLives';
	if (!firstName || !lastName || !workEmail) return 'info';
	if (!company || !companySize || !jobTitle) return 'company';
	return 'payment';
};

const PLACEHOLDER_COVERED_LIVES_VAL = 5;

const Teams: FC = () => {
	// Use function initializer to avoid hydration mismatch - this runs only on client mount
	const [hash, setHash] = useState<TeamsPageNameHash | ''>(() => {
		if (typeof window === 'undefined') return '';
		return (window.location.hash as TeamsPageNameHash) || '';
	});
	const activePage = getActivePage(hash);

	const { logEvent } = useAnalytics();
	const [coveredLivesVal, setCoveredLivesVal] = useState<CoveredLivesCount>();
	const [infoPageFields, setInfoPageFields] = useState<InfoFormFields>();
	const [companyPageFields, setCompanyPageFields] = useState<CompanyFormFields>();
	const { coupon } = useDiscountedPricing(coveredLivesVal ?? PLACEHOLDER_COVERED_LIVES_VAL);
	const completedStep1 = !_.isEmpty(infoPageFields ?? {});
	const completedStep2 = !_.isEmpty(companyPageFields ?? {});
	const [didInitializePage, setDidInitializePage] = useState(false);

	// Re-add hashchange listener to keep hash state in sync with URL
	useEffect(() => {
		const handleHashChange = (): void => {
			setHash(window.location.hash as TeamsPageNameHash);
		};
		window.addEventListener('hashchange', handleHashChange);
		return () => window.removeEventListener('hashchange', handleHashChange);
	}, []);

	const setActivePage = useCallback(async (page: TeamsPageName): Promise<void> => {
		if (window.fbq) {
			// Post route changes to Facebook Pixel
			window.fbq('track', 'ViewContent', {
				content_category: 'teams checkout',
				content_name: page,
			});
		}
		// Use native browser navigation for hash changes since router.replace() doesn't trigger hashchange
		window.location.hash = page;
	}, []);

	useEffect(() => {
		logEvent('Teams Page : Loaded', {
			location: activePage,
			variant: '7-16-24',
			selectedCoveredLives: coveredLivesVal ?? PLACEHOLDER_COVERED_LIVES_VAL,
			selectedContractedLives: calculatingSelectedContractedLives(
				coveredLivesVal ?? PLACEHOLDER_COVERED_LIVES_VAL,
			),
			...(companyPageFields?.companySize ? { companySize: companyPageFields.companySize } : {}),
		});
	}, [activePage, logEvent, coveredLivesVal, companyPageFields]);

	useStoreSsCheckoutProgress(didInitializePage, infoPageFields, companyPageFields, coveredLivesVal);

	useEffect(() => {
		const initializePage = async (): Promise<void> => {
			setDidInitializePage(true);

			const cookie = CalmCookie.get(TEAMS_USER_INFO_KEY);
			const cookieData = cookie ? JSON.parse(cookie) : null;
			const sessionStorageRawData = sessionStorage.getItem(TEAMS_USER_INFO_KEY);
			const sessionStorageData = sessionStorageRawData ? JSON.parse(sessionStorageRawData) : null;

			const { firstName, lastName, workEmail, company, companySize, jobTitle, coveredLives } =
				cookieData ?? sessionStorageData ?? {};

			if (coveredLives) {
				setCoveredLivesVal(Number(coveredLives));
			}

			if (firstName || lastName || workEmail) {
				setInfoPageFields(prevFields => ({
					...prevFields,
					firstName: firstName || prevFields?.firstName || '',
					lastName: lastName || prevFields?.lastName || '',
					email: workEmail || prevFields?.email || '',
				}));
			}

			if (company || companySize || jobTitle) {
				setCompanyPageFields(prevFields => ({
					...prevFields,
					companyName: company || prevFields?.companyName || '',
					companySize: companySize || prevFields?.companySize || '',
					role: jobTitle || prevFields?.role || '',
				}));
			}

			const navigateToPage = determineNavigateToPage({
				firstName,
				lastName,
				workEmail,
				company,
				companySize,
				jobTitle,
				coveredLives: Number(coveredLives),
			});

			if (cookieData) {
				logEvent('Teams Page : Marketing User', {
					location: navigateToPage,
					variant: '7-16-24',
					selectedContractedLives: calculatingSelectedContractedLives(
						coveredLivesVal ?? PLACEHOLDER_COVERED_LIVES_VAL,
					),
				});

				CalmCookie.remove(TEAMS_USER_INFO_KEY, { path: '/', domain: '.calm.com' });
			}

			if (activePage !== `#${navigateToPage}`) {
				await setActivePage(navigateToPage);
			}
		};

		if (!didInitializePage) {
			initializePage().catch(err => {
				calmLogger.error('Error initializing teams page', {}, err);
			});
		}
	}, [activePage, setActivePage, logEvent, coveredLivesVal, didInitializePage]);

	const navigateTo: NavigateTo = async (pageName, formModelFields) => {
		if (formModelFields && !_.isEmpty(formModelFields)) {
			if ('coveredLives' in formModelFields) {
				// Coming from the Covered Lives page
				const { coveredLives } = formModelFields;
				setCoveredLivesVal(coveredLives as CoveredLivesCount);
			} else if ('email' in formModelFields) {
				// Coming from the Info page
				const { email, firstName, lastName } = formModelFields;
				setInfoPageFields({ email, firstName, lastName } as InfoFormFields);
			} else if ('companyName' in formModelFields) {
				// Coming from the Company page
				const { companyName, companySize, role } = formModelFields;
				setCompanyPageFields({ companyName, companySize, role } as CompanyFormFields);
			}
		}

		await setActivePage(pageName);
	};

	const isOneTimeCoupon = coupon?.duration === 'once';
	const isPaymentPage = activePage === '#payment';
	const isCoveredLivesPage = activePage === '#coveredLives';
	const isInfoPage = activePage === '#info';
	const isCompanyPage = activePage === '#company';

	return (
		<ThemeProvider theme={CalmTheme}>
			<Wrapper>
				<InitialPageAnalytics />
				<Script src={`${EnvConfig.marketoBaseUrl}/js/forms2/js/forms2.min.js`} />
				<FixedTopBar data-testid="self-serve-fixed-top-bar">
					<CalmLogo />
				</FixedTopBar>
				<Container isCoveredLivesPage={isCoveredLivesPage} isPaymentPage={isPaymentPage}>
					<TeamsHero
						activePage={activePage}
						coveredLives={coveredLivesVal ?? PLACEHOLDER_COVERED_LIVES_VAL}
						navigateTo={navigateTo}
						completedStep1={completedStep1}
						completedStep2={completedStep2}
						oneTimeCoupon={isOneTimeCoupon}
					/>
					<Body>
						<Content role="main">
							{isCoveredLivesPage && (
								<CoveredLives
									navigateTo={navigateTo}
									defaultValue={coveredLivesVal ?? PLACEHOLDER_COVERED_LIVES_VAL}
								/>
							)}
							{isInfoPage && (
								<Info
									navigateTo={navigateTo}
									defaultValues={infoPageFields}
									coveredLives={coveredLivesVal ?? PLACEHOLDER_COVERED_LIVES_VAL}
								/>
							)}
							{isCompanyPage && (
								<Company
									navigateTo={navigateTo}
									defaultValues={companyPageFields}
									infoFields={infoPageFields}
									coveredLives={coveredLivesVal ?? PLACEHOLDER_COVERED_LIVES_VAL}
								/>
							)}
							{isPaymentPage && infoPageFields && companyPageFields && (
								<PaymentWithPaymentElement
									selectedCoveredLives={coveredLivesVal ?? PLACEHOLDER_COVERED_LIVES_VAL}
									infoPageFields={infoPageFields}
									companyPageFields={companyPageFields}
									setActivePage={setActivePage}
									coupon={coupon?.id}
								/>
							)}
						</Content>
					</Body>
				</Container>
			</Wrapper>
		</ThemeProvider>
	);
};

export default Teams;
